import { useEffect, useSyncExternalStore } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getDb } from '@talk2nick/shared';

/**
 * 번역 사용량 스토어 (PLAN 5-7-5장).
 *
 * 서버는 `translationUsage/{uid}_{YYYY-MM-DD}`에 실제 소모 문자를 기록하지만,
 * 그 값을 매 번역마다 다시 읽으면 Firestore 읽기가 번역 수만큼 늘어난다.
 * 그래서 **세션당 1회만 getDoc**하고, 이후에는 클라이언트가 낙관적으로 누적한다:
 *
 *  - 캐시 미스로 실제 번역이 성공하면 `noteTranslationChars(text.length)`
 *  - 서버 캐시 히트는 과금이 없으므로 세지 않는다
 *
 * 스토어를 모듈 싱글턴으로 두어 한도 배너·대화방·알림 호스트가 같은 숫자를 본다.
 */

export interface UsageState {
  chars: number;
  /** 이 값이 0이면 한도 정보를 아직 모른다(설정 로드 전) */
  limit: number;
  day: string;
  ready: boolean;
}

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

let state: UsageState = { chars: 0, limit: 0, day: todayKey(), ready: false };
const listeners = new Set<() => void>();
let loadedUid: string | null = null;
let inflight: Promise<void> | null = null;

function emit(): void {
  for (const listener of listeners) listener();
}

function setState(next: UsageState): void {
  state = next;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): UsageState {
  return state;
}

/** 설정(일일 한도)에서 온 값 — 스토어가 한도 도달 여부를 스스로 판정할 수 있게 한다 */
export function setTranslationLimit(limit: number): void {
  if (limit <= 0 || state.limit === limit) return;
  setState({ ...state, limit });
}

/** 실제 번역 성공(캐시 미스) 시 호출 — 대략적인 값으로 한도 UI를 즉시 움직인다 */
export function noteTranslationChars(chars: number): void {
  if (!Number.isFinite(chars) || chars <= 0) return;
  setState({ ...state, chars: state.chars + chars });
}

/** 오늘 한도를 다 썼는가 — 번역 큐가 네트워크 왕복 전에 걸러낸다 (PLAN 5-7-5장) */
export function isTranslationLimited(): boolean {
  return state.limit > 0 && state.chars >= state.limit;
}

function ensureLoaded(uid: string): void {
  const day = todayKey();
  // 날짜가 바뀌면 서버가 새 문서를 쓴다 — 로컬도 리셋한다
  if (state.day !== day) {
    loadedUid = null;
    setState({ chars: 0, limit: state.limit, day, ready: false });
  }
  if (loadedUid === uid && (state.ready || inflight)) return;
  loadedUid = uid;

  inflight = getDoc(doc(getDb(), 'translationUsage', `${uid}_${day}`))
    .then((snap) => {
      setState({
        chars: (snap.data()?.chars as number) ?? 0,
        limit: state.limit,
        day,
        ready: true,
      });
    })
    .catch(() => {
      // Rules/오프라인 실패는 조용히 넘어간다 — 한도 표시만 0으로 남는다
      setState({ chars: 0, limit: state.limit, day, ready: true });
    })
    .finally(() => {
      inflight = null;
    });
}

export interface TranslationUsage {
  chars: number;
  limit: number;
  /** 0~1 (한도 초과 시 1로 클램프) */
  ratio: number;
  /** 한도 도달 — 자동 번역을 멈추고 직접 번역만 남긴다 */
  reached: boolean;
}

export function useTranslationUsage(uid: string | null, limit: number): TranslationUsage {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (uid) ensureLoaded(uid);
  }, [uid]);

  useEffect(() => {
    if (limit > 0) setTranslationLimit(limit);
  }, [limit]);

  const ratio = limit > 0 ? Math.min(1, snapshot.chars / limit) : 0;
  return {
    chars: snapshot.chars,
    limit,
    ratio,
    reached: ratio >= 1,
  };
}
